import type { Query } from "@tanstack/query-core";
import { QueryClient, QueryKey } from "@tanstack/react-query";
import { PersistQueryClientProviderProps } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { AsyncStorage, Persister } from "@tanstack/query-persist-client-core";
import { isLogoutInProgress } from "../session/logoutState";

const browserWindow =
  typeof globalThis !== "undefined" && "window" in globalThis
    ? (globalThis as typeof globalThis & { window: Window }).window
    : undefined;

const asyncLocalStorage: AsyncStorage<string> | undefined =
  browserWindow?.localStorage
    ? {
        getItem: async (key: string) =>
          browserWindow.localStorage.getItem(key),
        setItem: async (key: string, value: string) =>
          browserWindow.localStorage.setItem(key, value),
        removeItem: async (key: string) =>
          browserWindow.localStorage.removeItem(key),
      }
    : undefined;

const containsPatientSegment = (queryKey: QueryKey): boolean => {
  const segments = Array.isArray(queryKey) ? queryKey : [queryKey];

  return segments.some((segment) => {
    if (typeof segment === "string") {
      return segment.toLowerCase().includes("patient");
    }

    if (typeof segment === "number") {
      return segment.toString().includes("patient");
    }

    if (Array.isArray(segment)) {
      return containsPatientSegment(segment);
    }

    if (typeof segment === "object" && segment !== null) {
      try {
        return JSON.stringify(segment)
          .toLowerCase()
          .includes("patient");
      } catch {
        return false;
      }
    }

    return false;
  });
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

const basePersister = createAsyncStoragePersister({
  storage: asyncLocalStorage,
  serialize: (clientState) => JSON.stringify(clientState),
  deserialize: (cachedString) => JSON.parse(cachedString),
});

const guardedPersister: Persister = {
  persistClient: async (clientState) => {
    if (isLogoutInProgress()) {
      return;
    }
    await basePersister.persistClient(clientState);
  },
  restoreClient: async () => basePersister.restoreClient(),
  removeClient: async () => basePersister.removeClient(),
};

export const persistOptions: PersistQueryClientProviderProps["persistOptions"] =
  {
    persister: guardedPersister,
    maxAge: 1000 * 60 * 30, // 30 minutes
    dehydrateOptions: {
      shouldDehydrateQuery: (query: Query) =>
        !containsPatientSegment(query.queryKey),
    },
  };

export const handlePersistSuccess: PersistQueryClientProviderProps["onSuccess"] =
  async () => {
    await queryClient.resumePausedMutations();
    await queryClient.refetchQueries({
      type: "active",
      stale: true,
    });
  };

export const clearPersistedQueryCache = async () => {
  queryClient.clear();
  await basePersister.removeClient();
  browserWindow?.localStorage?.removeItem("REACT_QUERY_OFFLINE_CACHE");
};

